/**
 * A sequencer to ensure that window (large area in an image) extraction operations are performed sequentially.
 * Indeed, when dealing with high resolution images, we are not able to extract all their content at once due to some browsers
 * and memory limitations. Therefore, we extract the image in smaller parts by dividing it into big regions that we will then
 * process to create some tiles. This sequencer ensures that only one window extraction operation is performed at a time.
 */
export class WindowExtractionSequencer {
  /** Indicates whether the previous window extraction operation is complete. */
  private previousWindowExtractionCompleted: boolean = true;
  /** Resolver for the next promise in the sequence. */
  private resolveNext?: () => void;
  /** Promise representing the completion of the previous window extraction operation. */
  private nextPromise: Promise<void> = Promise.resolve();

  /**
   * Waits for the previous window extraction operation to complete.
   */
  async waitForPreviousWindowToComplete(): Promise<void> {
    await this.nextPromise;
  }

  /**
   * Marks the start of a new window extraction operation.
   */
  startWindowExtraction(): void {
    this.previousWindowExtractionCompleted = false;
    this.nextPromise = new Promise<void>((resolve) => {
      this.resolveNext = resolve;
    });
  }

  /**
   * Marks the completion of the current window extraction operation.
   */
  setWindowExtractionAsCompleted(): void {
    this.previousWindowExtractionCompleted = true;
    if (this.resolveNext) {
      this.resolveNext();
      this.resolveNext = undefined;
    }
  }

  /**
   * Checks if the previous window extraction operation is complete.
   *
   * @returns True if the previous window extraction is complete, false otherwise.
   */
  isPreviousWindowExtractionCompleted(): boolean {
    return this.previousWindowExtractionCompleted;
  }
}
